import { Input, InputProps, Text } from '@/components';
import useOnClickOrScrollOutside from '@/lib/hooks/useOnClickOrScrollOutside';
import { get } from 'lodash';
import { useRef } from 'react';
import {
  DeepMap,
  FieldError,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormClearErrors,
  UseFormRegister,
} from 'react-hook-form';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border border-white'
  : '';
const formInputSpacingClasses = {
  formInputGap: 'gap-0',
  descriptionPadding: 'p-0',
};
const formInputSizingClasses = {
  inputWidth: 'w-full',
};
const formInputColorClasses = {
  descriptionTextColor: 'secondary' as const,
};
const formInputTextSizes = {
  descriptionTextSize: 'sm' as const,
};
export type FormInputProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  rules?: RegisterOptions<TFormValues, Path<TFormValues>>;
  register?: UseFormRegister<TFormValues>;
  clearErrors?: UseFormClearErrors<TFormValues>;
  errors?: Partial<DeepMap<TFormValues, FieldError>>;
  description?: string;
  showError?: boolean;
  value?: any;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & Omit<InputProps, 'name'>;
const FormInput = <TFormValues extends Record<string, any>>({
  name,
  register,
  rules,
  errors,
  className,
  description,
  type = 'text',
  showError = true,
  value,
  onChange,
  clearErrors,
  ...props
}: FormInputProps<TFormValues>): JSX.Element => {
  const error = get(errors, name as string);
  const hasError = !!error;
  const errorMessage = hasError ? error.message : undefined;
  const inputRef = useRef<HTMLDivElement>(null);
  useOnClickOrScrollOutside(inputRef, () => {
    if (hasError && clearErrors) {
      clearErrors(name);
    }
  });
  return (
    <div
      ref={inputRef}
      className={`flex flex-col ${formInputSpacingClasses.formInputGap} ${
        formInputSizingClasses.inputWidth
      } ${borderClassToVisualizeElementsAndSpacing} ${className ?? ''}`}
      aria-live="polite"
    >
      <Input
        name={name}
        className={`${borderClassToVisualizeElementsAndSpacing}`}
        intent={hasError ? 'error' : undefined}
        aria-invalid={hasError}
        showError={hasError}
        step={type === 'number' ? 'any' : undefined}
        type={type}
        defaultValue={value}
        value={value}
        onChange={(e) => {
          onChange?.(e);
        }}
        errorMessage={errorMessage}
        {...props}
        {...(register && register(name, rules))}
      />
      {description && (
        <Text
          value={description}
          size={formInputTextSizes.descriptionTextSize}
          color={formInputColorClasses.descriptionTextColor}
          className={`${formInputSpacingClasses.descriptionPadding} ${borderClassToVisualizeElementsAndSpacing}`}
        />
      )}
    </div>
  );
};
export default FormInput;
